import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import '../BulkAddOrganizations/css/index.scss';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import PageTitle from '../../components/PageTitle';
import MyButton from '../../components/MyButton';
import MyModal from '../../components/MyModal';
import Api from '../../Api';
import sendRequest from '../../helpers/sendRequest';
import { getCountriesRequest, getLanguagesRequest } from '../../store/actions/staticData';
import {
  EVENT_STATUSES,
  buildBulkEventsFileContent,
  buildEventDraftFormData,
  buildInitialBulkEventConfig,
  fetchCitiesOfCountryCached,
  fetchEventCategories,
  getEventConfigIssues,
  normalizeBulkEventConfig,
  parseBulkEventsJson,
  validateEventDraft,
} from '../../helpers/eventsBulkAutofill';
import { applyFieldValue } from './constants';
import BulkSetupPanel from './components/BulkSetupPanel';
import BulkUploadPanel from '../BulkAddOrganizations/components/BulkUploadPanel';
import BulkToolbar from './components/BulkToolbar';
import EventCard from './components/EventCard';
import EventEditModal from './components/EventEditModal';

function downloadTextFile(content, filename) {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function safeFileNamePart(value) {
  return String(value || '').replace(/[^\w-]+/g, '_').slice(0, 40);
}

function BulkAddEvents() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { countries, languages } = useSelector((store) => store.staticData);

  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [config, setConfig] = useState(buildInitialBulkEventConfig);
  const [drafts, setDrafts] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [downloadError, setDownloadError] = useState('');
  const [uploadText, setUploadText] = useState('');
  const [parseLoading, setParseLoading] = useState(false);
  const [parseError, setParseError] = useState('');
  const [isSetupOpen, setIsSetupOpen] = useState(true);
  const [editingUid, setEditingUid] = useState('');
  const [creatingProgress, setCreatingProgress] = useState('');
  const [confirmState, setConfirmState] = useState(null);
  const [resultText, setResultText] = useState('');

  const draftsRef = useRef(drafts);
  draftsRef.current = drafts;

  const normalizedConfig = useMemo(() => normalizeBulkEventConfig(config), [config]);
  const configIssues = useMemo(() => getEventConfigIssues(config), [config]);
  const staticLoading = categoriesLoading || !countries?.length || !languages?.length;

  const errorsByUid = useMemo(
    () => drafts.reduce((acc, draft) => ({ ...acc, [draft.uid]: validateEventDraft(draft) }), {}),
    [drafts],
  );
  const stats = useMemo(() => ({
    total: drafts.length,
    selected: selectedIds.length,
    created: drafts.filter((draft) => draft.status === EVENT_STATUSES.created).length,
    ready: drafts.filter((draft) => draft.status !== EVENT_STATUSES.created && !errorsByUid[draft.uid]?.length).length,
    invalid: drafts.filter((draft) => draft.status !== EVENT_STATUSES.created && errorsByUid[draft.uid]?.length).length,
  }), [drafts, selectedIds, errorsByUid]);

  const editingDraft = drafts.find((draft) => draft.uid === editingUid) || null;
  const editingIndex = drafts.findIndex((draft) => draft.uid === editingUid);

  const patchDraft = (uid, patch) => setDrafts((prev) => prev.map(
    (draft) => (draft.uid === uid ? { ...draft, ...patch } : draft),
  ));

  const loadReferences = async () => {
    const cities = normalizedConfig.country
      ? await fetchCitiesOfCountryCached(normalizedConfig.country._id)
      : [];
    return { categories, languages, cities };
  };

  const onDownloadFile = async () => {
    setDownloadError('');
    setDownloadLoading(true);
    try {
      const references = await loadReferences();
      const content = buildBulkEventsFileContent({ config: normalizedConfig, references });
      const nameParts = [normalizedConfig.country?.name, normalizedConfig.city?.name, normalizedConfig.category?.name]
        .filter(Boolean)
        .map(safeFileNamePart);
      downloadTextFile(content, `nomad-bulk-events${nameParts.length ? `-${nameParts.join('-')}` : ''}.txt`);
    } catch (e) {
      setDownloadError('Не удалось собрать файл: справочники не загрузились. Проверьте сеть и повторите.');
    } finally {
      setDownloadLoading(false);
    }
  };

  const onParseResponse = async () => {
    setParseError('');
    setParseLoading(true);
    try {
      const references = await loadReferences();
      const result = parseBulkEventsJson(uploadText, { config: normalizedConfig, references });
      if (result.error) {
        setParseError(result.error);
        return;
      }
      setDrafts((prev) => [...prev, ...result.drafts]);
      setUploadText('');
      setIsSetupOpen(false);
    } catch (e) {
      setParseError('Не удалось обработать ответ: справочники не загрузились. Проверьте сеть и повторите.');
    } finally {
      setParseLoading(false);
    }
  };

  const removeDrafts = (uids) => {
    setDrafts((prev) => prev.filter((draft) => !uids.includes(draft.uid)));
    setSelectedIds((prev) => prev.filter((uid) => !uids.includes(uid)));
  };

  const createDraft = async (uid) => {
    const draft = draftsRef.current.find((item) => item.uid === uid);
    if (!draft || draft.status === EVENT_STATUSES.created) return false;
    const errors = validateEventDraft(draft);
    if (errors.length) {
      patchDraft(uid, { status: EVENT_STATUSES.error, statusText: 'Заполните поля с ошибками — мероприятие не отправлено.' });
      return false;
    }
    patchDraft(uid, { status: EVENT_STATUSES.creating, statusText: '' });
    const data = await sendRequest({
      request: Api.createEvent,
      payload: buildEventDraftFormData(draft),
      warnErrorText: 'while creating event from bulk list',
    });
    if (data?.status !== 'ok') {
      const message = typeof data?.message === 'string' ? data.message : 'Не удалось установить соединение с сервером.';
      patchDraft(uid, { status: EVENT_STATUSES.error, statusText: message });
      return false;
    }
    patchDraft(uid, {
      status: EVENT_STATUSES.created,
      statusText: '',
      createdId: data.event?._id || data.organization?._id || '',
    });
    setSelectedIds((prev) => prev.filter((item) => item !== uid));
    return true;
  };

  const createSelected = async () => {
    const uids = draftsRef.current
      .filter((draft) => selectedIds.includes(draft.uid) && draft.status !== EVENT_STATUSES.created)
      .map((draft) => draft.uid);
    if (!uids.length) return;
    let created = 0;
    for (let i = 0; i < uids.length; i += 1) {
      setCreatingProgress(`Создание: ${i + 1} из ${uids.length}…`);
      const isCreated = await createDraft(uids[i]);
      if (isCreated) created += 1;
    }
    setCreatingProgress('');
    const failed = uids.length - created;
    setResultText([
      `Создано мероприятий: ${created} из ${uids.length}.`,
      failed ? `Не создано: ${failed} — причина указана в карточке.` : '',
    ].filter(Boolean).join('\n\n'));
  };

  const askConfirm = (text, onConfirm) => setConfirmState({ text, onConfirm });

  useEffect(() => {
    dispatch(getCountriesRequest());
    dispatch(getLanguagesRequest());
    let cancelled = false;
    fetchEventCategories()
      .then((list) => { if (!cancelled) setCategories(list); })
      .catch(() => { if (!cancelled) setCategories([]); })
      .finally(() => { if (!cancelled) setCategoriesLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!drafts.length) return undefined;
    const handler = (ev) => {
      ev.preventDefault();
      ev.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [drafts.length]);

  return (
    <div className="bulk_offers">
      <PageTitle
        text="Массовое создание мероприятий"
        RightComponent={(
          <div className="bulk_offers_header_buttons">
            {drafts.length ? (
              <MyButton
                text={isSetupOpen ? 'Скрыть настройки' : 'Режим и файл'}
                onClick={() => setIsSetupOpen((prev) => !prev)}
                secondary
                className="bulk_offers_header_button"
              />
            ) : null}
            {drafts.length ? (
              <MyButton
                text="Очистить список"
                onClick={() => askConfirm(
                  `Убрать все карточки (${drafts.length})? Созданные мероприятия останутся в системе.`,
                  () => { setDrafts([]); setSelectedIds([]); },
                )}
                secondary
                className="bulk_offers_header_button"
              />
            ) : null}
            <MyButton
              text="Отменить"
              onClick={() => {
                if (!drafts.length) {
                  navigate('/events/view');
                  return;
                }
                askConfirm(
                  `В списке ещё ${drafts.length} карточек. Уйти с экрана и потерять их?`,
                  () => navigate('/events/view'),
                );
              }}
              secondary
              className="bulk_offers_header_button"
            />
          </div>
        )}
      />

      <div className="bulk_offers_content">
        {isSetupOpen ? (
          <div className="bulk_offers_top">
            <BulkSetupPanel
              config={config}
              setConfig={setConfig}
              categories={categories}
              countries={countries || []}
              staticLoading={staticLoading}
              locked={Boolean(drafts.length)}
              onDownload={onDownloadFile}
              downloadLoading={downloadLoading}
              downloadError={downloadError}
            />
            <BulkUploadPanel
              value={uploadText}
              onChange={setUploadText}
              onSubmit={onParseResponse}
              loading={parseLoading}
              error={parseError}
              disabled={staticLoading || Boolean(configIssues.length)}
              disabledText={staticLoading
                ? 'Справочники ещё загружаются…'
                : 'Сначала задайте режим слева: по нему карточки получают владельца и закреплённые поля.'}
              placeholder="Вставьте JSON с массивом events…"
            />
          </div>
        ) : null}

        {drafts.length ? (
          <>
            <BulkToolbar
              stats={stats}
              config={normalizedConfig}
              categories={categories}
              countries={countries || []}
              languages={languages || []}
              creatingProgress={creatingProgress}
              onSelectAll={() => setSelectedIds(drafts.filter((draft) => draft.status !== EVENT_STATUSES.created).map((draft) => draft.uid))}
              onSelectNone={() => setSelectedIds([])}
              onCreateSelected={() => askConfirm(
                `Создать выбранные мероприятия (${selectedIds.length})? Действие необратимо.`,
                () => { createSelected().then(); },
              )}
              onRemoveSelected={() => askConfirm(
                `Убрать выбранные карточки (${selectedIds.length})?`,
                () => removeDrafts(selectedIds),
              )}
              onRemoveCreated={() => askConfirm(
                `Убрать из списка уже созданные карточки (${stats.created})?`,
                () => removeDrafts(drafts.filter((draft) => draft.status === EVENT_STATUSES.created).map((draft) => draft.uid)),
              )}
              onApplyToSelected={(field, value) => {
                setDrafts((prev) => prev.map((draft) => {
                  if (!selectedIds.includes(draft.uid) || draft.status === EVENT_STATUSES.created) return draft;
                  return applyFieldValue(draft, field, value);
                }));
              }}
            />
            <div className="bulk_offers_scroll">
              {drafts.map((draft, index) => (
                <EventCard
                  key={draft.uid}
                  draft={draft}
                  index={index}
                  errors={errorsByUid[draft.uid] || []}
                  isSelected={selectedIds.includes(draft.uid)}
                  onToggleSelect={() => setSelectedIds((prev) => (
                    prev.includes(draft.uid) ? prev.filter((item) => item !== draft.uid) : [...prev, draft.uid]
                  ))}
                  onCreate={() => askConfirm(
                    `Создать мероприятие «${draft.name || 'без названия'}»?`,
                    () => { createDraft(draft.uid).then(); },
                  )}
                  onRemove={() => askConfirm(
                    `Убрать карточку «${draft.name || 'без названия'}»?`,
                    () => removeDrafts([draft.uid]),
                  )}
                  onEdit={() => setEditingUid(draft.uid)}
                />
              ))}
            </div>
          </>
        ) : null}
      </div>

      <EventEditModal
        isOpen={Boolean(editingDraft)}
        draft={editingDraft}
        index={editingIndex}
        config={normalizedConfig}
        categories={categories}
        countries={countries || []}
        languages={languages || []}
        onClose={() => setEditingUid('')}
        onSave={(nextDraft) => {
          patchDraft(nextDraft.uid, { ...nextDraft, status: EVENT_STATUSES.draft, statusText: '' });
          setEditingUid('');
        }}
      />

      <MyModal isOpen={Boolean(confirmState)} closeFunction={() => setConfirmState(null)} style={{ width: 'min(480px, 92vw)' }}>
        <div className="bulk_offers_confirm">
          <p className="bulk_offers_confirm_text">{confirmState?.text}</p>
          <div className="bulk_offers_confirm_actions">
            <MyButton text="Отмена" onClick={() => setConfirmState(null)} secondary className="bulk_offers_modal_button" />
            <MyButton
              text="Подтвердить"
              onClick={() => {
                confirmState?.onConfirm?.();
                setConfirmState(null);
              }}
              className="bulk_offers_modal_button"
            />
          </div>
        </div>
      </MyModal>

      <MyModal isOpen={Boolean(resultText)} closeFunction={() => setResultText('')} style={{ width: 'min(480px, 92vw)' }}>
        <div className="bulk_offers_confirm">
          <pre className="bulk_offers_confirm_text">{resultText}</pre>
          <div className="bulk_offers_confirm_actions">
            <MyButton text="Ок" onClick={() => setResultText('')} className="bulk_offers_modal_button" />
          </div>
        </div>
      </MyModal>
    </div>
  );
}

export default BulkAddEvents;
